import { useEffect, useState } from 'react'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { useInfiniteQuery } from 'react-query'
import { useRouterScroll } from '@moxy/next-router-scroll'
import { NFDAuctionAndPrice, nfdBrowse, NfdBrowseParams, NfdRecordCollection } from 'api/api-client'
import useGetAuctions from 'api/hooks/useGetAuctions'
import useBrowseLimit from 'hooks/useBrowseLimit'
import useWallet from './useWallet'

type PagedBrowseData = {
  results: NfdRecordCollection
  nextPage: number | null
}

export default function useBrowsePage(query: NfdBrowseParams) {
  const [fetchedPages, setFetchedPages] = useState(new Set<number>())

  useEffect(() => {
    setFetchedPages(new Set())
  }, [query])

  const getByQuery = async (
    query: NfdBrowseParams,
    page: number,
    limit: number,
    ssr?: boolean,
    options?: AxiosRequestConfig
  ): Promise<PagedBrowseData> => {
    setFetchedPages((prev) => new Set(prev.add(page)))

    const { data: results } = await nfdBrowse(
      {
        ...query,
        ...(query.minPrice ? { minPrice: Number(query.minPrice) } : {}),
        ...(query.maxPrice ? { maxPrice: Number(query.maxPrice) } : {}),
        limit,
        offset: (page - 1) * limit,
        view: 'brief'
      },
      {
        ...(ssr && { headers: { origin: process.env.NEXT_PUBLIC_BASE_URL as string } }),
        ...options
      }
    )

    return {
      results,
      nextPage: results.length === limit ? page + 1 : null
    }
  }

  const { limit, enabled } = useBrowseLimit()

  const browseQuery = useInfiniteQuery<PagedBrowseData, AxiosError>(
    ['browse', { query, limit }],
    ({ pageParam = 1 }) => getByQuery(query, pageParam, limit),
    {
      enabled,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      getNextPageParam: (lastPage) => lastPage.nextPage
    }
  )

  const [auctions, setAuctions] = useState<NFDAuctionAndPrice[]>([])

  const auctionsQuery = useGetAuctions({
    startingSoon: true,
    options: {
      refetchInterval: auctions.length > 0 ? 60000 : 0
    }
  })

  useEffect(() => {
    if (auctionsQuery.data) {
      setAuctions(auctionsQuery.data)
    } else {
      setAuctions([])
    }
  }, [auctionsQuery.data])

  const getAuction = (name: string) => auctions.find((auction) => auction.auctionInfo.name === name)

  const wallet = useWallet()
  const activeWalletAddress = wallet.getAddress()

  const [hasSetScrollPosition, setHasSetScrollPosition] = useState(false)

  const { updateScroll } = useRouterScroll()

  useEffect(() => {
    if (browseQuery.isFetched && !hasSetScrollPosition) {
      updateScroll()
      setHasSetScrollPosition(true)
    }
  }, [hasSetScrollPosition, browseQuery.isFetched, updateScroll])

  return {
    browseQuery,
    fetchedPages,
    setFetchedPages,
    getAuction,
    activeWalletAddress
  }
}
